import React, { useMemo } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { KnowledgeBaseFileRow } from '@features/Admin/KnowledgeBase/ui/KnowledgeBaseFileRow';
import { KNOWLEDGE_BASE_PROCESSING_ERROR_KEY } from '@features/Admin/KnowledgeBase/model/error-mapper';

import { useStyles } from '../KnowledgeBaseTab.styles';
import type { KnowledgeBaseFileCardProps } from '../KnowledgeBaseTab.types';
import KnowledgeBaseOriginalActions from './KnowledgeBaseOriginalActions';

const KnowledgeBaseFileCard: React.FC<KnowledgeBaseFileCardProps> = ({
  file,
  agents,
  canManageFiles,
  isBusy,
  onDelete,
  onDetails,
  onToggle,
  onRetry,
}) => {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const agentsById = useMemo(() => new Map(agents.map((agent) => [agent.id, agent])), [agents]);
  const attachedAgents = useMemo(
    () =>
      file.agentIds.map((agentId) => ({
        id: agentId,
        name: agentsById.get(agentId)?.name || agentId,
      })),
    [agentsById, file.agentIds],
  );
  const isFailed = file.status === 'failed';
  const hasAllAgentAccess = file.agentScope === 'all';
  const statusClass =
    file.status === 'ready'
      ? classes.statusReady
      : isFailed
        ? classes.statusFailed
        : classes.statusProcessing;

  return (
    <KnowledgeBaseFileRow
      file={file}
      metadataDate="createdAt"
      cardProps={{ className: classes.fileCard }}
      contentProps={{ className: classes.fileCardContent }}
      infoProps={{ className: classes.fileInfo }}
      metadataProps={{ className: classes.fileMetadata }}
      actions={
        <Stack direction="row" useFlexGap className={classes.fileActions}>
          <Chip
            size="small"
            label={t(`campaignPage.knowledgeBase.status.${file.status}`)}
            className={cx(classes.statusChip, statusClass)}
          />
          {canManageFiles && file.status === 'ready' && (
            <FormControlLabel
              labelPlacement="start"
              className={classes.enabledControl}
              control={
                <Switch
                  size="small"
                  checked={file.isEnabled}
                  onChange={(event) => onToggle(file, event.target.checked)}
                  disabled={isBusy}
                  inputProps={{
                    'aria-label': `${file.fileName}: ${t('campaignPage.knowledgeBase.actions.enabled')}`,
                  }}
                />
              }
              label={
                file.isEnabled
                  ? t('campaignPage.knowledgeBase.actions.enabled')
                  : t('campaignPage.knowledgeBase.actions.disabled')
              }
            />
          )}
          <Button
            size="small"
            variant="contained"
            onClick={() => onDetails(file)}
            disabled={isBusy}
            className={classes.detailsButton}>
            {t('campaignPage.knowledgeBase.actions.details')}
          </Button>
          <KnowledgeBaseOriginalActions
            fileId={file.id}
            fileName={file.fileName}
            hasOriginal={file.hasOriginal}
            canManageFiles={canManageFiles}
            showDownload={false}
          />
          {isFailed && (
            <Tooltip title={t('campaignPage.knowledgeBase.actions.retry')}>
              <span>
                <IconButton
                  size="small"
                  aria-label={t('campaignPage.knowledgeBase.actions.retry')}
                  onClick={() => onRetry(file)}
                  disabled={isBusy}
                  className={classes.actionButton}>
                  <RefreshOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canManageFiles && (
            <Tooltip title={t('campaignPage.knowledgeBase.delete.action')}>
              <span>
                <IconButton
                  size="small"
                  aria-label={t('campaignPage.knowledgeBase.delete.action')}
                  onClick={() => onDelete(file)}
                  disabled={isBusy}
                  className={classes.deleteButton}>
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      }>
      {isFailed && (
        <Typography variant="caption" role="alert" className={classes.fileError}>
          {t(KNOWLEDGE_BASE_PROCESSING_ERROR_KEY)}
        </Typography>
      )}
      {(hasAllAgentAccess || attachedAgents.length > 0 || file.agentScope === 'selected') && (
        <Stack direction="row" flexWrap="wrap" useFlexGap className={classes.agentList}>
          {hasAllAgentAccess ? (
            <Chip
              size="small"
              label={t('campaignPage.knowledgeBase.assignments.allAgentsSummary')}
              className={classes.agentChip}
            />
          ) : attachedAgents.length > 0 ? (
            attachedAgents.map((agent) => (
              <Chip key={agent.id} size="small" label={agent.name} className={classes.agentChip} />
            ))
          ) : (
            <Chip
              size="small"
              label={t('campaignPage.knowledgeBase.assignments.noAgentAccess')}
              className={classes.agentChip}
            />
          )}
        </Stack>
      )}
    </KnowledgeBaseFileRow>
  );
};

export default KnowledgeBaseFileCard;
